import { toast } from "sonner";
import { useState } from "react";
import {
  Loader2, Sparkles, Upload, Link2, Import, Check, Search, User, Trash2, Cloud, CloudOff, TrendingUp, FileCode2, FolderOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CmsFieldWrap from "@/components/admin/CmsFieldWrap";
import { PropertyRightmoveSection } from "@/components/admin/PropertyRightmoveSection";
import { GalleryEditor } from "@/components/admin/GalleryEditor";
import { ImageUpload } from "@/components/ImageUpload";
import { resolveImageUrl } from "@/utils/imageUrl";
import { EstimatePriceModal } from "@/components/property-admin/EstimatePriceModal";
import { PlanningApplicationsModal } from "@/components/property-admin/PlanningApplicationsModal";
import DocumentsSection from "@/components/property-admin/DocumentsSection";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import {
  RIGHTMOVE_PROPERTY_TYPES,
  LEGACY_PROPERTY_TYPE_MAP,
} from "@/constants/rightmovePropertyTypes";

/**
 * Master Admin's "Add/Edit Property" modal — extracted from AdminDashboard.jsx
 * (pure move, no behavior change) to keep that file's size manageable. Mirrors
 * the same extraction pattern already used for the agent-facing equivalent
 * (`PropertyEditDialog.jsx`). All state/handlers are owned by the parent and
 * passed in as props.
 */
export function MasterPropertyEditDialog({
  showPropertyDialog,
  setShowPropertyDialog,
  editingProperty,
  cmsFieldSettings,
  sectionOrderIdx,
  propertyForm,
  setPropertyForm,
  propertyHasChanges,
  setPropertyHasChanges,
  handleFormChange,
  scrapeUrl,
  setScrapeUrl,
  scraping,
  handleScrape,
  scrapeAutoApply,
  setScrapeAutoApply,
  scrapeResult,
  setScrapeResult,
  applyScrapedData,
  handleGalleryUpload,
  generatingSeo,
  handleGenerateSeoKeywords,
  agents,
  handleSelectAgent,
  showDbNames,
  aiSummarising,
  handleAISummary,
  aiRewriting,
  handleAIRewrite,
  handleAddressGeocode,
  handlePostcodeGeocode,
  formLoading,
  handleSubmitProperty,
  handleDeleteProperty,
  propertyAutoSaving,
  propertyLastSaved,
  propertyInitialLoad,
}) {
  const [showEstimateModal, setShowEstimateModal] = useState(false);
  const [showPlanningAppsModal, setShowPlanningAppsModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const { styles: siteStylesData } = useSiteStyles();
  const estimatePriceCtaEnabled = siteStylesData?.estimatePriceCtaEnabled !== false;
  const planningAppsCtaEnabled = siteStylesData?.planningAppsCtaEnabled !== false;
  const documentsCtaEnabled = siteStylesData?.ctaDocumentsEnabled !== false;
  return (
    <Dialog open={showPropertyDialog} onOpenChange={setShowPropertyDialog}>
      <DialogContent
        className="sm:max-w-3xl max-h-[90vh] flex flex-col p-0 rounded-none"
        data-testid="property-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <DialogTitle className="font-serif text-2xl">
              {editingProperty ? "Edit Property" : "Add New Property"}
            </DialogTitle>
            {editingProperty?.property_id && documentsCtaEnabled && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs rounded-none"
                onClick={() => setShowDocumentsModal(true)}
                disabled={!propertyForm.agent_id}
                title={!propertyForm.agent_id ? "Assign an agent to this property first" : ""}
                data-testid="property-dialog-documents-btn"
              >
                <FolderOpen className="h-3.5 w-3.5" />
                Documents
              </Button>
            )}
          </div>
        </DialogHeader>

        <Dialog open={showDocumentsModal} onOpenChange={setShowDocumentsModal}>
          <DialogContent className="sm:max-w-5xl max-h-[85vh] flex flex-col rounded-none" data-testid="property-documents-dialog">
            <DialogHeader>
              <DialogTitle>Documents — {propertyForm.title || editingProperty?.title || "Property"}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              {showDocumentsModal && (
                <DocumentsSection agentId={propertyForm.agent_id} propertyId={editingProperty?.property_id} />
              )}
            </div>
          </DialogContent>
        </Dialog>

        <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-6">
          <div className="flex flex-col gap-4">
            <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="title" label="Title" required className="col-span-2" style={{ order: sectionOrderIdx("title") }}>
              <Input
                name="title"
                value={propertyForm.title}
                onChange={handleFormChange}
                placeholder="Property title"
                className="rounded-none"
                data-testid="property-title-input"
              />
            </CmsFieldWrap>

            {/* Live Status Section */}
            <div className="col-span-2 bg-muted/30 border border-border rounded-lg p-4 space-y-4" style={{ order: sectionOrderIdx("listing_status") }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${
                    propertyForm.status === "For Sale" ? "bg-green-500" :
                    propertyForm.status === "Test"     ? "bg-amber-500" :
                                                          "bg-red-500"
                  }`} />
                  <div>
                    <h4 className="font-medium text-sm">Listing Status</h4>
                    <p className="text-xs text-muted-foreground" data-testid="property-status-description">
                      {propertyForm.status === "For Sale" && "Property visible on the public website"}
                      {propertyForm.status === "Test"     && "Hidden from public — visible in staging / preview only"}
                      {propertyForm.status === "Off"      && "Property hidden everywhere"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div
                    className="inline-flex border border-border bg-background rounded-md p-0.5"
                    role="radiogroup"
                    aria-label="Listing status"
                    data-testid="property-status-segmented"
                  >
                    {["Off", "Test", "For Sale"].map((opt) => {
                      const selected = propertyForm.status === opt;
                      const activeCls = opt === "For Sale"
                        ? "bg-green-600 text-white"
                        : opt === "Test"
                          ? "bg-amber-500 text-white"
                          : "bg-gray-500 text-white";
                      return (
                        <button
                          key={opt}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => {
                            setPropertyForm(prev => ({
                              ...prev,
                              status: opt,
                              is_live: opt === "For Sale",
                            }));
                            if (editingProperty) setPropertyHasChanges(true);
                          }}
                          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                            selected ? activeCls : "text-muted-foreground hover:text-foreground"
                          }`}
                          data-testid={`property-status-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Featured</span>
                    <button
                      type="button"
                      onClick={() => {
                        setPropertyForm(prev => ({ ...prev, featured: !prev.featured }));
                        if (editingProperty) setPropertyHasChanges(true);
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        propertyForm.featured ? 'bg-amber-500' : 'bg-gray-300'
                      }`}
                      data-testid="property-featured-toggle"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        propertyForm.featured ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Live From</Label>
                  <Input
                    name="live_from"
                    type="date"
                    value={propertyForm.live_from || ''}
                    onChange={handleFormChange}
                    className="rounded-none h-9"
                    data-testid="property-live-from"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Live Until</Label>
                  <Input
                    name="live_to"
                    type="date"
                    value={propertyForm.live_to || ''}
                    onChange={handleFormChange}
                    className="rounded-none h-9"
                    data-testid="property-live-to"
                  />
                  <p className="text-xs text-muted-foreground">Default: 12 months</p>
                </div>
              </div>
            </div>

            {/* Import from URL Section */}
            <div className="col-span-2 p-4 bg-blue-50 border border-blue-200 rounded-sm space-y-3" style={{ order: sectionOrderIdx("import_url") }}>
              <div className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-blue-600" />
                <Label className="text-blue-800 font-medium">Import from URL</Label>
              </div>
              <p className="text-xs text-blue-600">
                Paste a property listing URL (Rightmove, Zoopla, etc.) to automatically import details
              </p>
              <div className="flex gap-2">
                <Input
                  type="url"
                  value={scrapeUrl}
                  onChange={(e) => setScrapeUrl(e.target.value)}
                  placeholder="https://www.rightmove.co.uk/properties/..."
                  className="flex-1 rounded-none text-sm"
                  data-testid="master-admin-scrape-url-input"
                />
                <Button
                  type="button"
                  onClick={handleScrape}
                  disabled={scraping || !scrapeUrl}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-none gap-2"
                  data-testid="master-admin-scrape-btn"
                >
                  {scraping ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Scraping...
                    </>
                  ) : (
                    <>
                      <Import className="h-4 w-4" />
                      Import
                    </>
                  )}
                </Button>
              </div>
              <label className="flex items-center gap-2 text-xs text-blue-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={scrapeAutoApply}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setScrapeAutoApply(next);
                    try {
                      localStorage.setItem("slh:scrape:auto-apply", next ? "1" : "0");
                    } catch (_) { /* localStorage unavailable — skip */ }
                  }}
                  data-testid="master-admin-scrape-auto-apply"
                />
                Apply immediately after import (skip the preview step)
              </label>

              {/* Scrape Results Preview */}
              {scrapeResult && (
                <div className="mt-4 p-4 bg-white border border-blue-200 rounded-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-blue-800">Scraped Data Preview</h4>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={applyScrapedData}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-none text-xs gap-1"
                        data-testid="master-admin-apply-scraped-data"
                      >
                        <Check className="h-3 w-3" />
                        Apply All
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setScrapeResult(null);
                          setScrapeUrl("");
                        }}
                        className="rounded-none text-xs"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {scrapeResult.title && (
                      <div className="p-2 bg-gray-50 rounded">
                        <strong>Title:</strong> {scrapeResult.title.substring(0, 40)}...
                      </div>
                    )}
                    {scrapeResult.price && (
                      <div className="p-2 bg-gray-50 rounded">
                        <strong>Price:</strong> £{scrapeResult.price?.toLocaleString()}
                      </div>
                    )}
                    {scrapeResult.beds && (
                      <div className="p-2 bg-gray-50 rounded">
                        <strong>Beds:</strong> {scrapeResult.beds}
                      </div>
                    )}
                    {scrapeResult.baths && (
                      <div className="p-2 bg-gray-50 rounded">
                        <strong>Baths:</strong> {scrapeResult.baths}
                      </div>
                    )}
                    {scrapeResult.display_address && (
                      <div className="p-2 bg-gray-50 rounded col-span-2">
                        <strong>Location:</strong> {scrapeResult.display_address}
                      </div>
                    )}
                    {scrapeResult.description && (
                      <div className="p-2 bg-gray-50 rounded col-span-2">
                        <strong>Description:</strong> {scrapeResult.description.substring(0, 80)}...
                      </div>
                    )}
                    {scrapeResult.images && scrapeResult.images.length > 0 && (
                      <div className="p-2 bg-gray-50 rounded col-span-2">
                        <strong>Images:</strong> {scrapeResult.images.length} found
                      </div>
                    )}
                    {scrapeResult.floorplan_image && (
                      <div className="p-2 bg-gray-50 rounded col-span-2">
                        <strong>Floorplan:</strong> Found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="price" label="Price" required className="col-span-2" style={{ order: sectionOrderIdx("price") }}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <span />
                <div className="flex items-center gap-2">
                  {editingProperty?.property_id && estimatePriceCtaEnabled && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1.5 text-xs rounded-none"
                      onClick={() => setShowEstimateModal(true)}
                      data-testid="estimate-price-btn"
                    >
                      <TrendingUp className="h-3.5 w-3.5" />
                      Estimate Price
                    </Button>
                  )}
                  {editingProperty?.property_id && planningAppsCtaEnabled && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1.5 text-xs rounded-none"
                      onClick={() => setShowPlanningAppsModal(true)}
                      data-testid="planning-applications-btn"
                    >
                      <FileCode2 className="h-3.5 w-3.5" />
                      Planning Applications
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <select
                  name="price_label"
                  value={propertyForm.price_label}
                  onChange={handleFormChange}
                  className="h-10 px-3 border border-input bg-background text-sm rounded-none"
                  data-testid="property-price-label-select"
                  title="Rightmove price qualifier — mapped to integer code on push."
                >
                  <option value="Default">Default</option>
                  <option value="POA">POA</option>
                  <option value="Guide Price">Guide Price</option>
                  <option value="Fixed Price">Fixed Price</option>
                  <option value="Offers in Excess of">Offers in Excess of</option>
                  <option value="OIRO">OIRO</option>
                  <option value="Sale by Tender">Sale by Tender</option>
                  <option value="From">From</option>
                  <option value="Shared Ownership">Shared Ownership</option>
                  <option value="Offers Over">Offers Over</option>
                  <option value="Part Buy Part Rent">Part Buy Part Rent</option>
                  <option value="Shared Equity">Shared Equity</option>
                </select>
                <select
                  name="currency"
                  value={propertyForm.currency || "£"}
                  onChange={handleFormChange}
                  className="h-10 px-2 border border-input bg-background text-sm rounded-none w-16"
                >
                  <option value="£">£</option>
                  <option value="$">$</option>
                  <option value="€">€</option>
                </select>
                <Input
                  name="price"
                  type="number"
                  value={propertyForm.price}
                  onChange={handleFormChange}
                  placeholder="950000"
                  className="rounded-none flex-1 min-w-[160px]"
                  data-testid="property-price-input"
                />
              </div>
            </CmsFieldWrap>

            <EstimatePriceModal
              propertyId={editingProperty?.property_id}
              open={showEstimateModal}
              onOpenChange={setShowEstimateModal}
              onUsePrice={(value) => {
                setPropertyForm((prev) => ({ ...prev, price: value }));
                toast.success(`Price set to £${value.toLocaleString("en-GB")}`);
              }}
            />

            <PlanningApplicationsModal
              propertyId={editingProperty?.property_id}
              open={showPlanningAppsModal}
              onOpenChange={setShowPlanningAppsModal}
            />


            <div className="col-span-2 border border-border rounded p-3 space-y-3" style={{ order: sectionOrderIdx("address") }}>
              <div className="text-sm font-medium text-muted-foreground">
                Address &amp; Location
              </div>
              <div className="grid grid-cols-2 gap-4">
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="display_address" label="Display Address" required className="col-span-2">
                  <Input
                    name="display_address"
                    value={propertyForm.display_address}
                    onChange={handleFormChange}
                    placeholder="City, State"
                    className="rounded-none"
                    data-testid="property-location-input"
                  />
                </CmsFieldWrap>
              </div>
            </div>

            {/* Bedrooms, Bathrooms & Parking Spaces are now edited under the
                Rightmove section (Rooms & Size / Amenities) — single source
                of truth so the same value drives the CMS and Rightmove. */}

            {/* New Property Details Fields — Size, Features and Description
                are now edited under Rightmove → Rooms & Size / Description
                so the same input drives both the CMS and Rightmove push. */}

            {/* General Details — property classification fields (type, tenure,
                council tax band, garden facing, date added, nearest stations).
                Wrapped in a single reorderable container so admins can move
                the whole block up or down in the CMS → Property Form Layout. */}
            <div className="col-span-2 grid grid-cols-2 gap-4" style={{ order: sectionOrderIdx("general_details") }}>
            <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="property_type" label="Property Type">
              <select
                name="property_type"
                value={LEGACY_PROPERTY_TYPE_MAP[propertyForm.property_type] || propertyForm.property_type || ""}
                onChange={handleFormChange}
                className="w-full h-10 px-3 border border-input bg-background rounded-none text-sm"
                data-testid="property-type-select"
                title="Choose the exact Rightmove property type — the label is sent as-is on push."
              >
                <option value="">Select type...</option>
                {RIGHTMOVE_PROPERTY_TYPES.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.values.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </CmsFieldWrap>

            <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="tenure" label="Tenure">
              <select
                name="tenure"
                value={propertyForm.tenure}
                onChange={handleFormChange}
                className="w-full h-10 px-3 border border-input bg-background rounded-none text-sm"
                data-testid="property-tenure-select"
              >
                <option value="">Select tenure...</option>
                <option value="freehold">Freehold</option>
                <option value="leasehold">Leasehold</option>
              </select>
            </CmsFieldWrap>

            <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="council_tax_band" label="Council Tax Band">
              <select
                name="council_tax_band"
                value={propertyForm.council_tax_band}
                onChange={handleFormChange}
                className="w-full h-10 px-3 border border-input bg-background rounded-none text-sm"
                data-testid="property-council-tax-select"
              >
                <option value="">Select band...</option>
                <option value="A">Band A</option>
                <option value="B">Band B</option>
                <option value="C">Band C</option>
                <option value="D">Band D</option>
                <option value="E">Band E</option>
                <option value="F">Band F</option>
                <option value="G">Band G</option>
                <option value="H">Band H</option>
              </select>
            </CmsFieldWrap>

            {/* Parking Spaces edited under Rightmove → Amenities (Parking). */}

            <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="garden_facing" label="Garden Facing">
              <select
                name="garden_facing"
                value={propertyForm.garden_facing}
                onChange={handleFormChange}
                className="w-full h-10 px-3 border border-input bg-background rounded-none text-sm"
                data-testid="property-garden-select"
              >
                <option value="">Select direction...</option>
                <option value="No Garden">No Garden</option>
                <option value="N">North</option>
                <option value="NE">North East</option>
                <option value="E">East</option>
                <option value="SE">South East</option>
                <option value="S">South</option>
                <option value="SW">South West</option>
                <option value="W">West</option>
                <option value="NW">North West</option>
              </select>
            </CmsFieldWrap>

            <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="date_added" label="Date Added">
              <Input
                name="date_added"
                type="date"
                value={propertyForm.date_added}
                onChange={handleFormChange}
                className="rounded-none"
                data-testid="property-date-input"
              />
            </CmsFieldWrap>

            <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="nearest_stations" label="Nearest Stations">
              <Input
                name="nearest_stations"
                value={propertyForm.nearest_stations}
                onChange={handleFormChange}
                placeholder="Victoria Line - 0.5 miles, Northern Line - 0.8 miles"
                className="rounded-none"
                data-testid="property-stations-input"
              />
            </CmsFieldWrap>
            </div>

            {/* Features moved to Rightmove → Description (Features field). */}

            {/* Description moved to Rightmove → Description panel. AI rewrite
                will follow the description field once it lands in the
                Rightmove section — for now stays available on the property
                card top toolbar. */}

            {/* Video Section */}
            <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="video_url" className="col-span-2 space-y-4" style={{ order: sectionOrderIdx("video") }}>
              <Label className="font-medium">Video Tour (optional)</Label>

              {/* CTA for Video Upload */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg space-y-3">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-600 rounded-lg">
                    <Upload className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-gray-900">Upload Property Video Tour</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Add a professional video tour (up to 50MB) or embed from YouTube, Instagram, or TikTok to showcase this property.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      // Scroll to gallery section where video upload happens
                      document.querySelector('[data-testid="master-admin-images-section"]')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Upload size={18} />
                    Upload Video (Max 50MB)
                  </button>
                  <span className="text-sm text-gray-500 self-center">or paste video URL below</span>
                </div>
              </div>

              {/* Video Type Selector */}
              <div className="flex items-center gap-4 p-3 bg-muted/30 border border-border">
                <span className="text-sm text-muted-foreground">Platform:</span>
                <div className="flex gap-2">
                  {[
                    { value: "youtube", label: "YouTube", icon: "📺" },
                    { value: "instagram", label: "Instagram", icon: "📷" },
                    { value: "tiktok", label: "TikTok", icon: "🎵" }
                  ].map((platform) => (
                    <button
                      key={platform.value}
                      type="button"
                      onClick={() => setPropertyForm(prev => ({ ...prev, video_type: platform.value }))}
                      className={`px-4 py-2 text-sm rounded-none border transition-colors ${
                        propertyForm.video_type === platform.value
                          ? "bg-primary text-white border-primary"
                          : "bg-white border-border hover:border-primary"
                      }`}
                      data-testid={`video-type-${platform.value}`}
                    >
                      {platform.icon} {platform.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Video URL Input */}
              <Input
                name="video_url"
                value={propertyForm.video_url}
                onChange={handleFormChange}
                placeholder={
                  propertyForm.video_type === "youtube" 
                    ? "https://www.youtube.com/watch?v=..." 
                    : propertyForm.video_type === "instagram"
                      ? "https://www.instagram.com/p/... or /reel/..."
                      : "https://www.tiktok.com/@user/video/..."
                }
                className="rounded-none"
                data-testid="property-video-input"
              />
              <p className="text-xs text-muted-foreground">
                Add a {propertyForm.video_type === "youtube" ? "YouTube" : propertyForm.video_type === "instagram" ? "Instagram" : "TikTok"} video tour for this property.
              </p>
            </CmsFieldWrap>

            {/* Show Video Thumbnail Toggle */}
            {propertyForm.video_url && (
              <div className="col-span-2 flex items-center gap-3 p-4 bg-muted/30 border border-border">
                <Switch
                  checked={propertyForm.show_video_thumbnail}
                  onCheckedChange={(checked) => 
                    setPropertyForm(prev => ({ ...prev, show_video_thumbnail: checked }))
                  }
                  data-testid="property-show-video-switch"
                />
                <div>
                  <Label className="font-medium">Show Video Thumbnail in Listings</Label>
                  <p className="text-xs text-muted-foreground">
                    {propertyForm.show_video_thumbnail 
                      ? "Listings will show video thumbnail with play button" 
                      : "Listings will show the main image"}
                  </p>
                </div>
              </div>
            )}

            {/* Page Display Options */}
            <div className="col-span-2 space-y-3 p-4 bg-muted/30 border border-border" style={{ order: sectionOrderIdx("page_display") }}>
              <Label className="font-medium">Property Page Display</Label>
              <p className="text-xs text-muted-foreground mb-3">
                Control what is shown on the property landing page
              </p>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">Show Website Header</Label>
                  <p className="text-xs text-muted-foreground">SellLocalHomes navigation</p>
                </div>
                <Switch
                  checked={propertyForm.show_header}
                  onCheckedChange={(checked) => 
                    setPropertyForm(prev => ({ ...prev, show_header: checked }))
                  }
                  data-testid="property-show-header-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">Show Website Footer</Label>
                  <p className="text-xs text-muted-foreground">SellLocalHomes footer section</p>
                </div>
                <Switch
                  checked={propertyForm.show_footer}
                  onCheckedChange={(checked) => 
                    setPropertyForm(prev => ({ ...prev, show_footer: checked }))
                  }
                  data-testid="property-show-footer-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">Show Back to Properties Link</Label>
                  <p className="text-xs text-muted-foreground">Link to return to listings</p>
                </div>
                <Switch
                  checked={propertyForm.show_back_link}
                  onCheckedChange={(checked) => 
                    setPropertyForm(prev => ({ ...prev, show_back_link: checked }))
                  }
                  data-testid="property-show-back-link-switch"
                />
              </div>
            </div>

            {/* Property Images - First image (by sort_order) is the featured image */}
            <div className="col-span-2 space-y-4" data-testid="master-admin-images-section" style={{ order: sectionOrderIdx("gallery") }}>
              <Label>Property Images ({propertyForm.gallery.length})</Label>
              <p className="text-xs text-muted-foreground">
                Use the uploader to add new files. Edit fields inline or drag rows to reorder. First (sort_order 0) = Featured.
              </p>
              <ImageUpload
                onUpload={handleGalleryUpload}
                multiple={true}
                currentImages={[]}
                label="Upload Images"
                skipWatermark={false}
              />

              <GalleryEditor
                value={propertyForm.gallery}
                onChange={(next) => {
                  setPropertyForm((prev) => ({ ...prev, gallery: next }));
                  setPropertyHasChanges(true);
                }}
                isMasterAdmin
                propertyId={editingProperty?.property_id}
              />

              {/* Featured Image Preview */}
              {propertyForm.gallery.length > 0 && (
                <div className="p-3 bg-muted/30 border border-border">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Featured Image Preview:</p>
                  <img 
                    src={resolveImageUrl((propertyForm.gallery[0] && propertyForm.gallery[0].media_url) || "")} 
                    alt="Featured" 
                    className="w-full max-w-md h-48 object-cover border border-primary"
                  />
                </div>
              )}
            </div>

            {/* Floorplan Image */}
            <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="floorplan_image" label="Floorplan" className="col-span-2" style={{ order: sectionOrderIdx("floorplan") }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  {propertyForm.floorplan_image && (
                    <div className="relative w-full">
                      <img 
                        src={resolveImageUrl(propertyForm.floorplan_image)} 
                        alt="Floorplan" 
                        className="w-full h-48 object-contain border border-border bg-muted/30"
                      />
                      <button
                        type="button"
                        onClick={() => setPropertyForm(prev => ({ ...prev, floorplan_image: "" }))}
                        className="absolute top-2 right-2 bg-destructive text-white p-1 rounded text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  <ImageUpload
                    onUpload={(url) => setPropertyForm(prev => ({ ...prev, floorplan_image: url }))}
                    multiple={false}
                    label="Upload Floorplan"
                  />
                </div>
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="floorplan_size" label="Floorplan Size (sq ft)">
                  <Input
                    name="floorplan_size"
                    value={propertyForm.floorplan_size}
                    onChange={handleFormChange}
                    placeholder="e.g., 2,500 sq ft"
                    className="rounded-none"
                    data-testid="property-floorplan-size-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Size to display next to the floorplan
                  </p>
                </CmsFieldWrap>
              </div>
            </CmsFieldWrap>

            {/* SEO Section */}
            <div className="col-span-2 space-y-4 p-4 bg-green-50/50 border border-green-200" style={{ order: sectionOrderIdx("seo") }}>
              <div className="border-b border-green-200 pb-3 mb-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-lg font-medium text-green-900 flex items-center gap-2">
                    <Search size={18} />
                    SEO Settings
                  </h3>
                  <Button
                    type="button"
                    size="sm"
                    className="rounded-none text-xs bg-purple-600 hover:bg-purple-700 text-white"
                    disabled={generatingSeo}
                    onClick={handleGenerateSeoKeywords}
                    data-testid="generate-seo-btn"
                  >
                    {generatingSeo ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                    AI Generate SEO
                  </Button>
                </div>
                <p className="text-xs text-green-700 mt-1">
                  Search engine optimization — AI generates title, description, keywords from property details
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="seo_title" label="SEO Title">
                  <Input
                    name="seo_title"
                    value={propertyForm.seo_title}
                    onChange={handleFormChange}
                    placeholder={propertyForm.title || "Property title for search engines"}
                    className="rounded-none"
                    data-testid="property-seo-title-input"
                  />
                  <p className="text-xs text-green-600">
                    {(propertyForm.seo_title || "").length}/60 characters
                  </p>
                </CmsFieldWrap>
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="seo_focus_keyword" label="Focus Keyword">
                  <Input
                    name="seo_focus_keyword"
                    value={propertyForm.seo_focus_keyword}
                    onChange={handleFormChange}
                    placeholder="e.g., 3 bed house london"
                    className="rounded-none"
                    data-testid="property-seo-focus-input"
                  />
                </CmsFieldWrap>
              </div>

              <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="seo_description" label="Meta Description">
                <Textarea
                  name="seo_description"
                  value={propertyForm.seo_description}
                  onChange={handleFormChange}
                  placeholder="Brief description for search engine results..."
                  className="rounded-none resize-none"
                  rows={2}
                  data-testid="property-seo-description-input"
                />
                <p className="text-xs text-green-600">
                  {(propertyForm.seo_description || "").length}/160 characters
                </p>
              </CmsFieldWrap>

              <div className="grid grid-cols-2 gap-4">
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="seo_slug" label="URL Slug">
                  <Input
                    name="seo_slug"
                    value={propertyForm.seo_slug}
                    onChange={handleFormChange}
                    placeholder="custom-url-slug (auto-generated if empty)"
                    className="rounded-none"
                    data-testid="property-seo-slug-input"
                  />
                </CmsFieldWrap>
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="seo_robots" label="Robots">
                  <select
                    name="seo_robots"
                    value={propertyForm.seo_robots}
                    onChange={handleFormChange}
                    className="w-full h-10 px-3 border border-input bg-background text-sm rounded-none"
                    data-testid="property-seo-robots-select"
                  >
                    <option value="index, follow">Index, Follow (Default)</option>
                    <option value="index, nofollow">Index, No Follow</option>
                    <option value="noindex, follow">No Index, Follow</option>
                    <option value="noindex, nofollow">No Index, No Follow</option>
                  </select>
                </CmsFieldWrap>
              </div>

              <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="seo_keywords" label="Keywords">
                <Textarea
                  name="seo_keywords"
                  value={propertyForm.seo_keywords}
                  onChange={handleFormChange}
                  placeholder="keyword1, keyword2, keyword3"
                  className="rounded-none"
                  rows={3}
                  data-testid="property-seo-keywords-input"
                />
                <p className="text-xs text-green-600">Comma-separated keywords</p>
              </CmsFieldWrap>
            </div>

            {/* Agent Details — assign an agent and (optionally) override any of
                their contact details for this listing only. Selecting an
                agent from the picker auto-fills the fields via
                `handleSelectAgent`, but each field remains editable. */}
            <div
              className="col-span-2 space-y-4 p-4 bg-indigo-50/60 border border-indigo-200"
              style={{ order: sectionOrderIdx("agent") }}
              data-testid="property-agent-section"
            >
              <div className="border-b border-indigo-200 pb-3 mb-2 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-serif text-lg font-medium text-indigo-900 flex items-center gap-2">
                    <User size={18} />
                    Agent Details
                  </h3>
                  <p className="text-xs text-indigo-700 mt-1">
                    Assign an agent to this property. Fields auto-fill from the agent record but can be overridden per listing.
                  </p>
                </div>
                {propertyForm.agent_logo && (
                  <img
                    src={propertyForm.agent_logo}
                    alt="Agent logo"
                    className="h-10 w-10 rounded-full object-cover border border-indigo-200 bg-white"
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Assigned agent</Label>
                  <select
                    value={propertyForm.agent_id || ""}
                    onChange={(e) => handleSelectAgent(e.target.value)}
                    className="w-full h-10 px-3 border border-input bg-background text-sm rounded-none"
                    data-testid="property-agent-select"
                  >
                    <option value="">— No agent —</option>
                    {agents.map((a) => (
                      <option key={a.agent_id} value={a.agent_id}>
                        {a.name}{a.email ? ` (${a.email})` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-muted-foreground">
                    Pick an agent to auto-fill their contact info. Manage the master agent list in the Agents tab.
                  </p>
                </div>

                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="agent_contact_name" label="Contact name">
                  <Input
                    name="agent_contact_name"
                    value={propertyForm.agent_contact_name || ""}
                    onChange={handleFormChange}
                    placeholder="Named contact for enquiries"
                    className="rounded-none"
                    data-testid="property-agent-contact-input"
                  />
                </CmsFieldWrap>
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="agent_tel" label="Phone">
                  <Input
                    name="agent_tel"
                    value={propertyForm.agent_tel || ""}
                    onChange={handleFormChange}
                    placeholder="+44 20 …"
                    className="rounded-none"
                    data-testid="property-agent-tel-input"
                  />
                </CmsFieldWrap>
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="agent_whatsapp_tel" label="WhatsApp Number">
                  <Input
                    name="agent_whatsapp_tel"
                    value={propertyForm.agent_whatsapp_tel || ""}
                    onChange={handleFormChange}
                    placeholder="Same as phone if blank"
                    className="rounded-none"
                    data-testid="property-agent-whatsapp-input"
                  />
                </CmsFieldWrap>
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="agent_whatsapp_enabled" className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm select-none">
                    <input
                      type="checkbox"
                      checked={propertyForm.agent_whatsapp_enabled !== false}
                      onChange={(e) => setPropertyForm((prev) => ({
                        ...prev, agent_whatsapp_enabled: e.target.checked,
                      }))}
                      className="h-4 w-4"
                      data-testid="property-agent-whatsapp-enabled-input"
                    />
                    Show &quot;Chat on WhatsApp&quot; button on this property
                  </label>
                </CmsFieldWrap>
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="agent_email" label="Email">
                  <Input
                    type="email"
                    name="agent_email"
                    value={propertyForm.agent_email || ""}
                    onChange={handleFormChange}
                    placeholder="agent@example.co.uk"
                    className="rounded-none"
                    data-testid="property-agent-email-input"
                  />
                </CmsFieldWrap>
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="agent_website" label="Website">
                  <Input
                    name="agent_website"
                    value={propertyForm.agent_website || ""}
                    onChange={handleFormChange}
                    placeholder="https://…"
                    className="rounded-none"
                    data-testid="property-agent-website-input"
                  />
                </CmsFieldWrap>
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="agent_commission" label="Commission">
                  <Input
                    name="agent_commission"
                    value={propertyForm.agent_commission || ""}
                    onChange={handleFormChange}
                    placeholder="e.g. 1.25% + VAT"
                    className="rounded-none"
                    data-testid="property-agent-commission-input"
                  />
                </CmsFieldWrap>
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="agent_email_notifications" className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm select-none">
                    <input
                      type="checkbox"
                      checked={propertyForm.agent_email_notifications !== false}
                      onChange={(e) => setPropertyForm((prev) => ({
                        ...prev, agent_email_notifications: e.target.checked,
                      }))}
                      className="h-4 w-4"
                      data-testid="property-agent-notify-input"
                    />
                    Send enquiry email notifications to this agent
                  </label>
                </CmsFieldWrap>
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="agent_notes" label="Internal notes" className="col-span-2">
                  <Textarea
                    name="agent_notes"
                    value={propertyForm.agent_notes || ""}
                    onChange={handleFormChange}
                    placeholder="Only visible in admin — commission terms, viewing instructions, etc."
                    className="rounded-none resize-none"
                    rows={2}
                    data-testid="property-agent-notes-input"
                  />
                </CmsFieldWrap>
              </div>
            </div>

            {/* Vendor Details — the property owner's contact info. Defaults
                from whoever submitted "List your property" but is fully
                editable, since the submitter (e.g. an agent) isn't always
                the actual vendor. Used to fill "Vendor contact details" on
                supplier order emails. */}
            <div
              className="col-span-2 space-y-4 p-4 bg-amber-50/60 border border-amber-200"
              style={{ order: sectionOrderIdx("vendor") }}
              data-testid="property-vendor-section"
            >
              <div className="border-b border-amber-200 pb-3 mb-2">
                <h3 className="font-serif text-lg font-medium text-amber-900 flex items-center gap-2">
                  <User size={18} />
                  Vendor Details
                </h3>
                <p className="text-xs text-amber-700 mt-1">
                  Contact details for the property owner — used in supplier order emails.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="vendor_name" label="Vendor Name">
                  <Input
                    name="vendor_name"
                    value={propertyForm.vendor_name || ""}
                    onChange={handleFormChange}
                    className="rounded-none"
                    data-testid="property-vendor-name-input"
                  />
                </CmsFieldWrap>
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="vendor_phone" label="Vendor Phone">
                  <Input
                    name="vendor_phone"
                    value={propertyForm.vendor_phone || ""}
                    onChange={handleFormChange}
                    className="rounded-none"
                    data-testid="property-vendor-phone-input"
                  />
                </CmsFieldWrap>
                <CmsFieldWrap settings={cmsFieldSettings} formData={propertyForm} fieldKey="vendor_email" label="Vendor Email" className="col-span-2">
                  <Input
                    type="email"
                    name="vendor_email"
                    value={propertyForm.vendor_email || ""}
                    onChange={handleFormChange}
                    className="rounded-none"
                    data-testid="property-vendor-email-input"
                  />
                </CmsFieldWrap>
              </div>
            </div>

          {/* Rightmove — per-property overrides for unmapped RTDF fields.
              Each RTDF group renders in its own <div> so the admin can drag
              any single group (e.g. Media) into any position in the modal
              via CMS → Property Form Layout. The component fetches its
              field catalogue once per mount; we pass `groupFilter` to make
              each instance render only its assigned group, and `hideHeader`
              so the "Rightmove · X of Y set" header doesn't repeat.
              Also proxies primary CMS fields (Address, Lat/Lng, Beds, Baths,
              Parking Spaces) so the admin edits them ONCE, here. */}
          {[
            { orderId: "rm_type_status",  group: "Type & Status"  },
            { orderId: "rm_address",      group: "Address"        },
            { orderId: "rm_dates",        group: "Dates"          },
            { orderId: "rm_price_tenure", group: "Price & Tenure" },
            { orderId: "rm_description",  group: "Description"    },
            { orderId: "rm_rooms_size",   group: "Rooms & Size"   },
            { orderId: "rm_amenities",    group: "Amenities"      },
            { orderId: "rm_regulatory",   group: "Regulatory"     },
            { orderId: "rm_media",        group: "Media"          },
          ].map(({ orderId, group }) => (
          <div
            key={orderId}
            style={{ order: sectionOrderIdx(orderId) }}
            data-testid={`rm-section-${orderId}`}
          >
          <PropertyRightmoveSection
            hideHeader
            groupFilter={group}
            showDbNames={showDbNames}
            overrides={propertyForm.cms_overrides}
            fieldExtras={{
              Summary: (
                <Button
                  type="button"
                  onClick={handleAISummary}
                  disabled={aiSummarising || !propertyForm.description}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold px-3 py-1 rounded-full shadow-sm hover:shadow-md transition-all duration-200 gap-1 text-xs h-6"
                  data-testid="ai-summary-btn"
                  title="Condense the description into a one-paragraph Rightmove summary"
                >
                  {aiSummarising ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Summarising…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3 w-3" />
                      AI
                    </>
                  )}
                </Button>
              ),
              Description: (
                <Button
                  type="button"
                  onClick={handleAIRewrite}
                  disabled={aiRewriting || !propertyForm.description}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold px-3 py-1 rounded-full shadow-sm hover:shadow-md transition-all duration-200 gap-1 text-xs h-6"
                  data-testid="ai-rewrite-btn"
                  title="Rewrite the description with AI"
                >
                  {aiRewriting ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Rewriting…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3 w-3" />
                      AI
                    </>
                  )}
                </Button>
              ),
            }}
            onChange={(next) => {
              setPropertyForm((prev) => ({ ...prev, cms_overrides: next }));
              if (editingProperty) setPropertyHasChanges(true);
            }}
            cmsValues={{
              cms_House_Name_Number: propertyForm.cms_House_Name_Number,
              street_name: propertyForm.street_name,
              postcode: propertyForm.postcode,
              transaction_status: propertyForm.transaction_status,
              latitude: propertyForm.latitude,
              longitude: propertyForm.longitude,
              beds: propertyForm.beds,
              baths: propertyForm.baths,
              description: propertyForm.description,
              features: propertyForm.features,
            }}
            onCmsChange={(cmsKey, value) => {
              setPropertyForm((prev) => ({ ...prev, [cmsKey]: value }));
              if (editingProperty) setPropertyHasChanges(true);
            }}
            onProxyBlur={(cmsKey) => {
              // Re-run postcode geocoding whenever the address input is
              // blurred so lat/lng stay in sync — same behaviour as the
              // legacy standalone Address 1 field used to have.
              if (cmsKey === "address") handleAddressGeocode();
            }}
            onProxyFocus={(cmsKey) => {
              // Recalculate lat/lng from the postcode field whenever the
              // admin focuses on it, so coordinates can be refreshed on
              // demand (force-refresh regardless of existing values).
              if (cmsKey === "postcode") handlePostcodeGeocode();
            }}
          />
          </div>
          ))}
          </div>
        </div>
        </div>

        {/* Fixed Footer with Action Buttons */}
        <div className="shrink-0 bg-background border-t border-border px-6 py-4 flex justify-between items-center gap-4">
          <Button
            variant="outline"
            onClick={() => {
              setShowPropertyDialog(false);
              setPropertyHasChanges(false);
              propertyInitialLoad.current = true;
            }}
            className="rounded-none"
          >
            {editingProperty ? "Close" : "Cancel"}
          </Button>

          {editingProperty ? (
            /* Auto-save status + Delete for editing */
            <div className="flex items-center justify-between w-full">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDeleteProperty(editingProperty?.property_id)}
                className="rounded-none border-red-300 text-red-600 hover:bg-red-50"
                data-testid="delete-property-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete Property
              </Button>
              <div className="flex items-center gap-2 text-sm" data-testid="property-autosave-status">
              {propertyAutoSaving ? (
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </span>
              ) : propertyHasChanges ? (
                <span className="flex items-center gap-1.5 text-amber-600">
                  <CloudOff className="h-4 w-4" />
                  <span>Unsaved changes</span>
                </span>
              ) : propertyLastSaved ? (
                <span className="flex items-center gap-1.5 text-green-600">
                  <Cloud className="h-4 w-4" />
                  <span>All changes saved</span>
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Cloud className="h-4 w-4" />
                  <span>Auto-save enabled</span>
                </span>
              )}
            </div>
            </div>
          ) : (
            /* Create button for new properties */
            <Button
              onClick={handleSubmitProperty}
              disabled={formLoading}
              className="bg-primary text-white hover:bg-primary/90 rounded-none px-8"
              data-testid="save-property-btn"
            >
              {formLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Property"
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MasterPropertyEditDialog;
